import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Loader, theme as grooveTheme } from '@groove-labs/groove-ui';
import Typography from '@material-ui/core/Typography';
import { withStyles, createStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';

import {
  makeGetReportIsValid,
  makeGetSelectedReportSfdcId,
  makeGetValidationInProgress,
  makeGetValidationIsDone,
} from 'Modules/PeopleImportDialog/submodules/reports/selectors/validateReport';
import { validateAutoImportReportBegin } from 'Modules/PeopleImportDialog/submodules/reports/actions/validateAutoImportReport';
import { REPORT_TYPES } from 'Modules/PeopleImportDialog/submodules/reports/constants';

const classes = {
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  loader: {
    left: 0,
    paddingRight: grooveTheme.spacing.unit,
    width: 'auto',
  },
  buttonRoot: {
    padding: '8px 0',
  },
  buttonText: {
    '&:hover': {
      background: 'transparent',
    },
  },
};

const styles = createStyles(classes);

const OBJECT_READABLE_NAMES = {
  [REPORT_TYPES.PERSON]: 'lead/contact',
  [REPORT_TYPES.OPPORTUNITY]: REPORT_TYPES.OPPORTUNITY,
  [REPORT_TYPES.ACCOUNT]: REPORT_TYPES.ACCOUNT,
};

type ValidationTriggerPropTypes = {
  classes: {
    root: string;
    loader: string;
    buttonRoot: string;
    buttonText: string;
  };
  objectType: string;
  groupId: string;
};

const ValidationTrigger: React.FC<ValidationTriggerPropTypes> = ({
  classes,
  groupId,
  objectType,
}) => {
  const reportSfdcId = useSelector(makeGetSelectedReportSfdcId(groupId));
  const validationInProgress = useSelector(
    makeGetValidationInProgress(reportSfdcId)
  );
  const validationIsDone = useSelector(makeGetValidationIsDone(reportSfdcId));
  const reportIsValid = useSelector(makeGetReportIsValid(reportSfdcId));

  const dispatch = useDispatch();
  const validateReport = React.useCallback(() => {
    dispatch(validateAutoImportReportBegin({ reportSfdcId, objectType }));
  }, [dispatch, reportSfdcId, objectType]);

  React.useEffect(() => {
    if (!validationIsDone && !validationInProgress) {
      dispatch(validateAutoImportReportBegin({ reportSfdcId, objectType }));
    }
  }, [
    dispatch,
    reportSfdcId,
    validationIsDone,
    validationInProgress,
    objectType,
  ]);

  if (!validationInProgress && reportIsValid) return null;

  if (!validationInProgress && !reportIsValid) {
    return (
      <Button
        color="primary"
        onClick={validateReport}
        classes={{
          root: classes.buttonRoot,
          textPrimary: classes.buttonText,
        }}
      >
        Rerun test
      </Button>
    );
  }

  const validationInProgressText = `Checking Report for ${OBJECT_READABLE_NAMES[objectType]} fields to import...`;

  return (
    <div>
      <div className={classes.root}>
        <Loader
          classes={{ transparentRoot: classes.loader }}
          size={16}
          transparentBackground
        />
        <Typography variant="caption">{validationInProgressText}</Typography>
      </div>
    </div>
  );
};

ValidationTrigger.displayName = 'ValidationTrigger';

export default withStyles(styles)(ValidationTrigger);
